import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with credentials
const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Global handler for 428 Precondition Required (photo_required gate)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 428) {
      const detail = error.response.data?.detail;
      const code = detail?.code || detail;
      if (code === 'photo_required') {
        // Avoid infinite loop if already on /photo-required
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/photo-required')) {
          // Preserve the intended path so we can return after they upload
          try { localStorage.setItem('gv_after_photo_return', window.location.pathname + window.location.search); } catch { /* ignore */ }
          window.location.assign('/photo-required');
        }
      }
    }
    return Promise.reject(error);
  }
);

// Profile APIs
export const getProfile = () => api.get('/profile');
export const updateProfile = (data) => api.put('/profile', data);
export const getProfiles = (params) => api.get('/profiles', { params });
export const getProfileById = (userId) => api.get(`/profiles/${userId}`);

// Photo Upload API
export const uploadPhoto = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload/photo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

// Discovery APIs
export const discoverProfiles = (limit = 10) => api.get('/discover', { params: { limit } });
export const swipeAction = (targetUserId, action) => api.post('/swipe', { target_user_id: targetUserId, action });
export const swipeUndo = () => api.post('/swipe/undo');

// Match APIs
export const getMatches = () => api.get('/matches');

// Message APIs
export const sendMessage = (recipientId, content) => api.post('/messages', { recipient_id: recipientId, content });
export const sendMessagePhoto = (matchId, file, caption = '') => {
  const formData = new FormData();
  formData.append('file', file);
  if (caption) formData.append('caption', caption);
  return api.post(`/messages/${matchId}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const sendMessageVoice = (matchId, file, durationSeconds = 0) => {
  const formData = new FormData();
  formData.append('file', file, file.name || 'voice.webm');
  formData.append('duration_seconds', String(durationSeconds));
  return api.post(`/messages/${matchId}/voice`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getMessages = (matchId, skip = 0, limit = 50) => api.get(`/messages/${matchId}`, { params: { skip, limit } });
export const getConversations = () => api.get('/conversations');
export const startConversation = (userId, initialMessage = '') =>
  api.post(`/messages/start/${userId}`, initialMessage ? { initial_message: initialMessage } : {});

// Settings APIs
export const getMessagingSettings = () => api.get('/settings/messaging');

// Report/Block APIs
export const reportUser = (reportedUserId, reason, details) => api.post('/report', { reported_user_id: reportedUserId, reason, details });
export const blockUser = (userId) => api.post(`/block/${userId}`);
export const getBlockedUsers = () => api.get('/blocks');

// Payment APIs
export const getPackages = () => api.get('/packages');
export const createStripeCheckout = (packageId, originUrl, promoCode) =>
  api.post('/payments/stripe/checkout', { package_id: packageId, origin_url: originUrl, promo_code: promoCode });
export const getStripePaymentStatus = (sessionId) => api.get(`/payments/stripe/status/${sessionId}`);
export const createPayPalOrder = (packageId, promoCode) =>
  api.post('/payments/paypal/create-order', { package_id: packageId, promo_code: promoCode });
export const capturePayPalOrder = (orderId, paypalOrderId) => api.post('/payments/paypal/capture-order', { order_id: orderId, paypal_order_id: paypalOrderId });

// Admin APIs
export const adminGetUsers = (params) => api.get('/admin/users', { params });
export const adminSuspendUser = (userId) => api.post(`/admin/users/${userId}/suspend`);
export const adminUnsuspendUser = (userId) => api.post(`/admin/users/${userId}/unsuspend`);
export const adminDeleteUser = (userId) => api.delete(`/admin/users/${userId}`);
export const adminGetReports = (status = 'pending') => api.get('/admin/reports', { params: { status } });
export const adminResolveReport = (reportId, resolution) => api.post(`/admin/reports/${reportId}/resolve`, { resolution });
export const adminGetSettings = () => api.get('/admin/settings');
export const adminUpdateSettings = (settings) => api.put('/admin/settings', settings);
export const adminGetAnalytics = () => api.get('/admin/analytics');

// Discovery Settings (public + admin)
export const getDiscoverySettings = () => api.get('/settings/discovery');
export const adminGetDiscoverySettings = () => api.get('/admin/discovery-settings');
export const adminUpdateDiscoverySettings = (payload) => api.put('/admin/discovery-settings', payload);

// Search / Saved Searches
export const searchProfiles = (filters, { skip = 0, limit = 24, count_only = false } = {}) =>
  api.post('/search', filters, { params: { skip, limit, count_only } });
export const getSavedSearches = () => api.get('/saved-searches');
export const createSavedSearch = (name, filters, notify = false) => api.post('/saved-searches', { name, filters, notify });
export const deleteSavedSearch = (id) => api.delete(`/saved-searches/${id}`);

// New Members
export const getNewMembers = (limit = 20, days) => api.get('/members/new', { params: { limit, days } });

// Discovery: online / new_members filters
export const discoverProfilesFiltered = (limit = 20, opts = {}) =>
  api.get('/discover', { params: { limit, ...opts } });

// Discovery Home Sections & Recommendations
export const getDiscoverySections = () => api.get('/discovery/sections');
export const getRecommendations = (limit = 12) => api.get('/matches/recommendations', { params: { limit } });
export const getProfileCompletion = () => api.get('/profile/completion');

// Virtual Gifts
export const getGifts = () => api.get('/gifts');
export const sendGift = (giftId, recipientId, message = '') =>
  api.post('/gifts/send', { gift_id: giftId, recipient_id: recipientId, message });
export const getReceivedGifts = () => api.get('/gifts/received');
export const adminListGifts = () => api.get('/admin/gifts');
export const adminCreateGift = (payload) => api.post('/admin/gifts', payload);
export const adminUpdateGift = (id, payload) => api.put(`/admin/gifts/${id}`, payload);
export const adminDeleteGift = (id) => api.delete(`/admin/gifts/${id}`);

// Boost
export const getBoostPackages = () => api.get('/boosts');
export const activateBoost = (boostId) => api.post(`/boosts/activate/${boostId}`);
export const adminListBoosts = () => api.get('/admin/boosts');
export const adminCreateBoost = (payload) => api.post('/admin/boosts', payload);
export const adminUpdateBoost = (id, payload) => api.put(`/admin/boosts/${id}`, payload);
export const adminDeleteBoost = (id) => api.delete(`/admin/boosts/${id}`);

// Icebreakers / Let's Connect
export const getIcebreakers = () => api.get('/icebreakers');
export const getIcebreakerRecommendations = (limit = 6) => api.get('/icebreakers/recommendations', { params: { limit } });
export const sendIcebreaker = (recipientIds, message) =>
  api.post('/icebreakers/send', { recipient_ids: recipientIds, message });

// Email verification
export const startEmailVerification = () => api.post('/verify/email/start');
export const confirmEmailVerification = (code) => api.post('/verify/email/confirm', { code });

// Private Photo access requests
export const requestPrivatePhotos = (targetUserId, message = '') =>
  api.post('/private-photos/request', { target_user_id: targetUserId, message });
export const respondPrivatePhotoRequest = (requestId, approve) =>
  api.post('/private-photos/respond', { request_id: requestId, approve });
export const listPrivatePhotoRequests = (direction = 'incoming') =>
  api.get('/private-photos/requests', { params: { direction } });

// Notifications
export const getNotifications = (unreadOnly = false, limit = 50) =>
  api.get('/notifications', { params: { unread_only: unreadOnly, limit } });
export const markNotificationsRead = () => api.post('/notifications/mark-read');

// Credit history
export const getCreditHistory = ({ skip = 0, limit = 100 } = {}) =>
  api.get('/credits/history', { params: { skip, limit } });

// Admin broadcasts
export const adminBroadcastPreview = (payload) => api.post('/admin/broadcasts/preview-count', payload);
export const adminListBroadcasts = ({ status, skip = 0, limit = 100 } = {}) =>
  api.get('/admin/broadcasts', { params: { status, skip, limit } });
export const adminGetBroadcast = (id) => api.get(`/admin/broadcasts/${id}`);
export const adminCreateBroadcast = (payload) => api.post('/admin/broadcasts', payload);
export const adminSendBroadcastNow = (id) => api.post(`/admin/broadcasts/${id}/send-now`);
export const adminCancelBroadcast = (id) => api.post(`/admin/broadcasts/${id}/cancel`);
export const adminResendBroadcast = (id) => api.post(`/admin/broadcasts/${id}/resend`);
export const adminDeleteBroadcast = (id) => api.delete(`/admin/broadcasts/${id}`);

// Admin credit adjust (already existing endpoint)
export const adminAdjustCredits = (userId, amount, reason) =>
  api.post(`/admin/users/${userId}/credits`, { amount, reason });

// Admin user search (existing)
export const adminSearchUsers = (search, { skip = 0, limit = 20 } = {}) =>
  api.get('/admin/users', { params: { search, skip, limit } });

// Admin photo moderation
export const adminGetPhotoReasons = () => api.get('/admin/photo-moderation/reasons');
export const adminListUserPhotos = (userId) => api.get(`/admin/users/${userId}/photos`);
export const adminRemovePhoto = (photoId, { reason_code, admin_note, require_replacement = true }) =>
  api.post(`/admin/photos/${photoId}/remove`, { reason_code, admin_note, require_replacement });
export const adminRemovePhotoUrl = (userId, { photo_url, reason_code, admin_note, require_replacement = true }) =>
  api.post(`/admin/users/${userId}/remove-photo-url`, { photo_url, reason_code, admin_note, require_replacement });

// Contact Us
export const submitContact = (payload) => api.post('/contact', payload);
export const adminListContactMessages = ({ status, unreadOnly = false, skip = 0, limit = 100 } = {}) =>
  api.get('/admin/contact-messages', { params: { status, unread_only: unreadOnly, skip, limit } });
export const adminGetContactMessage = (id) => api.get(`/admin/contact-messages/${id}`);
export const adminMarkContactMessage = (id, patch) => api.post(`/admin/contact-messages/${id}/mark`, patch);
export const adminDeleteContactMessage = (id) => api.delete(`/admin/contact-messages/${id}`);

// Profile View
export const recordProfileView = (userId, source) => api.post(`/profiles/${userId}/view`, { source });
export const getLikesReceived = ({ skip = 0, limit = 30 } = {}) => api.get('/likes/received', { params: { skip, limit } });
export const getViewsReceived = ({ skip = 0, limit = 30 } = {}) => api.get('/views/received', { params: { skip, limit } });
export const unlockUserPhotos = (targetUserId) => api.post(`/unlock-user/${targetUserId}`);

// Heartbeat (mark self online)
export const sendHeartbeat = () => api.post('/heartbeat');

// Welcome Images
export const getWelcomeImagesPublic = () => api.get('/settings/welcome-images');
export const adminListWelcomeImages = () => api.get('/admin/welcome-images');
export const adminUploadWelcomeImage = (file, caption = '', altText = '') => {
  const formData = new FormData();
  formData.append('file', file);
  const params = {};
  if (caption) params.caption = caption;
  if (altText) params.alt_text = altText;
  return api.post('/admin/welcome-images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params: Object.keys(params).length ? params : undefined
  });
};
export const adminUpdateWelcomeImage = (imageId, payload) => api.put(`/admin/welcome-images/${imageId}`, payload);
export const adminReorderWelcomeImages = (orderIds) => api.put('/admin/welcome-images/reorder', { order: orderIds });
export const adminDeleteWelcomeImage = (imageId) => api.delete(`/admin/welcome-images/${imageId}`);

// Promotional Credits
export const adminListPromoCredits = () => api.get('/admin/promotional-credits');
export const adminCreatePromoCredit = (payload) => api.post('/admin/promotional-credits', payload);
export const adminUpdatePromoCredit = (promoId, payload) => api.put(`/admin/promotional-credits/${promoId}`, payload);
export const adminActivatePromoCredit = (promoId) => api.post(`/admin/promotional-credits/${promoId}/activate`);
export const adminDeactivatePromoCredit = (promoId) => api.post(`/admin/promotional-credits/${promoId}/deactivate`);
export const adminDeletePromoCredit = (promoId) => api.delete(`/admin/promotional-credits/${promoId}`);
export const adminGetPromoCreditGrants = (promoId, { skip = 0, limit = 100 } = {}) =>
  api.get(`/admin/promotional-credits/${promoId}/grants`, { params: { skip, limit } });
export const adminGetPromoCreditsHistory = ({ skip = 0, limit = 100 } = {}) =>
  api.get('/admin/promotional-credits/grants-history', { params: { skip, limit } });

export default api;
